import React, { FC, useState, PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
import { Heading, Link, Text, Box } from '@lucid/components-react';

import Person, { PersonColor, PersonData } from './person';
import { Component, LucidTheme } from 'types/types';

export interface PeopleSectionProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Heading for the PeopleSection */
  heading?: string;
  /** Subheading for the PeopleSection */
  subheading?: string;
  /** Sets which people should be displayed */
  people: PersonData[];
  /** Sets number of people to display before the show more */
  showMoreLimit?: number;
  /** Sets the color scheme for the people backgrounds */
  colorScheme?: PersonColor[];
  /** The available variants for the PeopleSection component. */
  variant?: Component.Type['PeopleSection'];
}

const StyledPeopleSection = styled.div(
  variant({
    scale: 'peopleSection',
    variants: {
      defaultVariant: {},
    },
  }),
);

const PeopleSection: FC<PropsWithChildren<PeopleSectionProps>> = ({
  className,
  heading,
  subheading,
  people,
  showMoreLimit,
  colorScheme = ['coolGrey'],
  variant = 'threeAcross',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  let peoplePerRow = 3;
  switch (variant) {
    case 'threeAcross':
      peoplePerRow = 3;
      break;
    case 'fourAcross':
      peoplePerRow = 4;
  }

  const getNextColor = (options: PersonColor[], exclude: PersonColor[]) => {
    options = options.filter(color => !exclude.includes(color));
    return options[Math.floor(Math.random() * options.length)];
  };

  // create an even distribution of color options
  let colorOptions = colorScheme;
  while (colorOptions.length < people.length) {
    colorOptions = [...colorOptions, ...colorScheme];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const peopleColors = people.reduce<PersonColor[]>((previous, _) => {
    if (colorScheme.length === 1) {
      return [...previous, colorScheme[0]];
    }
    // get a color different from adjacent colors
    const colorToLeft = previous.length % peoplePerRow === 0 ? undefined : previous[previous.length - 1];
    const colorAbove = previous[previous.length - peoplePerRow];
    const color = getNextColor(colorOptions, colorToLeft ? [colorToLeft, colorAbove] : [colorAbove]);
    // remove selected color from color options to ensure even distribution
    colorOptions.splice(colorOptions.indexOf(color), 1);
    return [...previous, color];
  }, []);

  return (
    <StyledPeopleSection className={className} variant={variant}>
      <Box
        css={(theme: LucidTheme) => ({
          maxWidth: '580px',
          textAlign: 'center',
          margin: 'auto',
          marginBottom: theme.space.s80,
        })}
      >
        {heading && (
          <Heading as="h2" variant="headline2XL">
            {heading}
          </Heading>
        )}
        {subheading && <Text variant="paragraphL400">{subheading}</Text>}
      </Box>
      <ul>
        {people?.slice(0, isExpanded ? people.length : showMoreLimit).map((person, index) => (
          <li key={`person-${index}`} css={{ margin: 0 }}>
            <Person backgroundColor={peopleColors[index]} person={person} />
          </li>
        ))}
      </ul>
      {showMoreLimit && !isExpanded && showMoreLimit < people.length && (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Link
            iconAnimation="expand"
            onClick={() => setIsExpanded(true)}
            onKeyDown={event => {
              if (['Space', 'Enter'].includes(event.key)) setIsExpanded(true);
            }}
            tabIndex={0}
          >
            Show more
          </Link>
        </Box>
      )}
    </StyledPeopleSection>
  );
};

export { PeopleSection };
export default PeopleSection;
